export type DraftSocketEvent = "open" | "message" | "close" | "error";

export interface DraftWebSocket {
  addEventListener(type: DraftSocketEvent, listener: EventListener): void;
  removeEventListener(type: DraftSocketEvent, listener: EventListener): void;
  close(): void;
}

export interface ReconnectingDraftSocketOptions {
  url: string;
  onMessage: () => void;
  createSocket?: (url: string) => DraftWebSocket;
  schedule?: (callback: () => void, delayMs: number) => unknown;
  cancel?: (handle: unknown) => void;
  initialDelayMs?: number;
  maximumDelayMs?: number;
  backoffFactor?: number;
}

export interface ReconnectingDraftSocket {
  dispose(): void;
}

interface ActiveConnection {
  socket: DraftWebSocket;
  listeners: Record<DraftSocketEvent, EventListener>;
}

const DEFAULT_INITIAL_DELAY_MS = 1_000;
const DEFAULT_MAXIMUM_DELAY_MS = 15_000;
const DEFAULT_BACKOFF_FACTOR = 2;

/** Keeps one best-effort realtime connection; snapshot polling stays active. */
export function createReconnectingDraftSocket(
  options: ReconnectingDraftSocketOptions,
): ReconnectingDraftSocket {
  const createSocket = options.createSocket ?? ((url: string) => new WebSocket(url));
  const schedule = options.schedule ?? ((callback, delayMs) => globalThis.setTimeout(callback, delayMs));
  const cancel = options.cancel ?? ((handle) => globalThis.clearTimeout(handle as ReturnType<typeof setTimeout>));
  const initialDelayMs = positiveNumber(options.initialDelayMs, DEFAULT_INITIAL_DELAY_MS);
  const maximumDelayMs = Math.max(initialDelayMs, positiveNumber(options.maximumDelayMs, DEFAULT_MAXIMUM_DELAY_MS));
  const backoffFactor = Math.max(1, positiveNumber(options.backoffFactor, DEFAULT_BACKOFF_FACTOR));

  let active: ActiveConnection | null = null;
  let retryHandle: unknown | null = null;
  let consecutiveFailures = 0;
  let disposed = false;

  function detach(connection: ActiveConnection): void {
    for (const type of ["open", "message", "close", "error"] as const) {
      connection.socket.removeEventListener(type, connection.listeners[type]);
    }
  }

  function retry(): void {
    if (disposed || retryHandle !== null) return;
    const delayMs = Math.min(maximumDelayMs, initialDelayMs * backoffFactor ** consecutiveFailures);
    consecutiveFailures += 1;
    retryHandle = schedule(() => {
      retryHandle = null;
      connect();
    }, delayMs);
  }

  function release(connection: ActiveConnection, closeSocket: boolean): void {
    if (active !== connection) return;
    active = null;
    detach(connection);
    if (closeSocket) {
      try { connection.socket.close(); } catch { /* The retry remains valid. */ }
    }
    retry();
  }

  function connect(): void {
    if (disposed || active !== null || retryHandle !== null) return;
    let socket: DraftWebSocket;
    try {
      socket = createSocket(options.url);
    } catch {
      retry();
      return;
    }

    let connection!: ActiveConnection;
    const listeners: Record<DraftSocketEvent, EventListener> = {
      open: () => { if (active === connection) consecutiveFailures = 0; },
      message: () => { if (active === connection && !disposed) options.onMessage(); },
      close: () => release(connection, false),
      error: () => release(connection, true),
    };
    connection = { socket, listeners };
    active = connection;
    try {
      for (const type of ["open", "message", "close", "error"] as const) {
        socket.addEventListener(type, listeners[type]);
      }
    } catch {
      release(connection, true);
    }
  }

  connect();

  return {
    dispose() {
      if (disposed) return;
      disposed = true;
      if (retryHandle !== null) {
        cancel(retryHandle);
        retryHandle = null;
      }
      const connection = active;
      active = null;
      if (connection) {
        detach(connection);
        try { connection.socket.close(); } catch { /* Teardown is best effort. */ }
      }
    },
  };
}

function positiveNumber(value: number | undefined, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : fallback;
}
